// @ts-strict-ignore
import Icon, { AppstoreFilled, ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import Avatar from 'antd/lib/avatar';
import Divider from 'antd/lib/divider';
import Skeleton from 'antd/lib/skeleton';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import * as React from 'react';
import { useSelector } from 'react-redux';
import appPlaceholderImage from '../../../assets/app-placeholder.png';
import { displayStatValue } from '../../helpers';
import { displayTextForAppPlatform } from '../../helpers/displayHelpers';
import { iconForPlatform } from '../../helpers/platformComponentHelpers';
import { RootState } from '../../reducers/reducers';
import { selectStatisticsById } from '../../selectors/selectors';
import { AppPlatform, StatisticCategory, StatisticType, StatisticUnit } from '../../types';
import { AppSet } from '../../typescriptHelpers';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { colorForStatCategory, displayTitleForStatCategory, iconForStatCategory } from './orgOverviewHelpers';
import Tabs from 'antd/lib/tabs';
import DatePickerTimeRange from '../DatePickerTimeRange';
import ConfigProvider from 'antd/lib/config-provider';
import { useThemeMode } from '../styles/themeMode';

export type AppPlatformOrAll = AppPlatform | 'all';
export type StatisticCategoryOrAll = StatisticCategory | 'all';
export interface OrgOverviewAppSetsTabBarProps {
    appSets: AppSet[];
    selectedAppSetIndex: number;
    onAppSetSelected: (appSetIndex: number) => void;
    selectedPlatform: AppPlatformOrAll;
    onPlatformSelected: (platform: AppPlatformOrAll, appId?: string) => void;
    selectedStatCategory: StatisticCategoryOrAll;
    onStatCategorySelected: (statCategory: StatisticCategoryOrAll) => void;
    isLoading: boolean;
    calendarDatesHaveChanged: boolean;
    onFinish: (values) => void;
}

export interface OrgOverviewAppMetricsTabBarProps {
    selectedStatCategory: StatisticCategoryOrAll;
    onStatCategorySelected: (statCategory: StatisticCategoryOrAll) => void;
}

const arrowForTrend: (trend: number) => React.ReactNode = (trend) => {
    let color = antGrey.grey6;

    if (trend > 0.01) {
        color = runwayColors.statusGreen;
    } else if (trend < 0 && Math.abs(trend) > 0.05) {
        color = runwayColors.statusRed;
    }

    let node: React.ReactNode;
    const fontSize = '18px';

    if (trend < 0) {
        node = <ArrowDownOutlined style={{ fontSize, color }} />;
    } else if (trend > 0) {
        node = <ArrowUpOutlined style={{ fontSize, color }} />;
    } else {
        node = <></>;
    }

    return node;
};

const AppSetTab: React.FC<
    AppSet & {
        isLoading: boolean;
        isSelected: boolean;
        onClick: () => void;
    }
> = ({ name, isSelected, apps, onClick, icon, isLoading, statsRequestId }) => {
    const statistics = useSelector((state: RootState) => {
        if (statsRequestId) {
            return selectStatisticsById(state, statsRequestId);
        }

        return null;
    });

    const stabilityStat = statistics?.find((stat) => stat.type === StatisticType.CrashFreeSessions);
    const ratingStat = statistics?.find((stat) => stat.type === StatisticType.StatAverageCustomerRating);

    const formattedRating =
        ratingStat?.value ?
            displayStatValue(StatisticType.StatAverageCustomerRating, ratingStat.value, StatisticUnit.Rating)
        :   '--';
    const formattedStability =
        stabilityStat?.value ?
            displayStatValue(StatisticType.CrashFreeSessions, stabilityStat.value, StatisticUnit.Percent)
        :   '--';

    const stabilityTrendArrow = stabilityStat?.trend ? arrowForTrend(stabilityStat.trend) : null;
    const ratingTrendArrow = ratingStat?.trend ? arrowForTrend(ratingStat.trend) : null;

    return (
        <div
            className="app-set-tab"
            onClick={onClick}
            style={{
                flexShrink: 0,
                padding: 23,
                display: 'flex',
                gap: 23,
                boxSizing: 'content-box',
                borderRadius: '8px 8px 0px 0px',
                ...(isSelected ? { backgroundColor: antGrey.grey3 } : {}),
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: 42,
                    height: 42,
                }}
            >
                {icon ?
                    <Icon
                        component={icon}
                        style={{
                            fontSize: 38,
                            transition: 'color 0.6s',
                            color: isSelected ? antGrey.grey5 : antGrey.grey4,
                        }}
                    />
                :   <div
                        style={{
                            position: 'relative',
                            transform: 'rotate(45deg)',
                            display: 'flex',
                            alignContent: 'stretch',
                            height: '100%',
                            width: 26,
                        }}
                    >
                        {apps.map((app, i) => (
                            <div
                                key={app.id}
                                style={{
                                    flex: 1,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Avatar
                                    shape="square"
                                    style={{
                                        position: 'absolute',
                                        borderRadius: 10,
                                        transform: `rotate(-45deg) ${apps.length > 1 ? 'scale(0.8)' : ''}`,
                                        filter: `drop-shadow(rgba(0, 0, 0, 0.2) 0px 4px 5px) ${
                                            i === apps.length - 1 ? '' : 'brightness(0.85)'
                                        }`,
                                    }}
                                    src={app.imageUrl || appPlaceholderImage}
                                    size={37}
                                />
                            </div>
                        ))}
                    </div>
                }
            </div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                }}
            >
                <Typography.Paragraph
                    style={{
                        fontSize: 16,
                        lineHeight: '24px',
                        maxWidth: 180,
                        margin: 0,
                        color: antGrey.grey8,
                        fontWeight: isSelected ? 600 : 400,
                    }}
                    ellipsis={{
                        rows: 1,
                        tooltip: name,
                    }}
                >
                    {name}
                </Typography.Paragraph>

                {isLoading ?
                    <Skeleton
                        className="monitoring-settings-skeleton"
                        active
                        title={{
                            width: 50,
                        }}
                        paragraph={false}
                    />
                :   !!statistics && (
                        <Space size={10}>
                            <Space>
                                {ratingTrendArrow}
                                <Typography.Text
                                    style={{
                                        color: antGrey.grey7,
                                    }}
                                >
                                    {formattedRating}
                                </Typography.Text>
                            </Space>
                            <Divider
                                type="vertical"
                                dashed
                                style={{
                                    borderColor: antGrey.grey6,
                                    margin: 0,
                                }}
                            />
                            <Space>
                                {stabilityTrendArrow}
                                <Typography.Text
                                    style={{
                                        color: antGrey.grey7,
                                    }}
                                >
                                    {formattedStability}
                                </Typography.Text>
                            </Space>
                        </Space>
                    )
                }
            </div>
        </div>
    );
};

const MetricCategoryTab: React.FC<{
    label: string;
    icon?: React.ReactNode;
    isSelected: boolean;
    isDark: boolean;
}> = ({ label, icon, isSelected, isDark }) => {
    return (
        <Space
            size={10}
            data-sidebar-theme={isDark ? 'dark' : 'light'}
            style={{
                padding: '10px 16px',
                borderRadius: '8px 8px 0 0',
                border: isSelected ? `1px solid ${antGrey.grey5}` : '1px solid transparent',
                backgroundColor: isSelected ? antGrey.grey3 : 'transparent',
                color: antGrey.grey8,
                fontWeight: isSelected ? 600 : 400,
            }}
        >
            {icon}
            {label}
        </Space>
    );
};

export interface FilterBarOption {
    label: string;
    value: string;
    icon?: React.ReactNode;
}

export interface FilterBarProps {
    options: FilterBarOption[];
    selectedOptionValue: string;
    onOptionSelected: (optionValue: string) => void;
}

export const FilterBar: React.FC<FilterBarProps> = ({ options, selectedOptionValue, onOptionSelected }) => {
    return (
        <Space size={2}>
            {options.map((option) => {
                const isSelected = selectedOptionValue === option.value;
                return (
                    <Space
                        key={option.value}
                        onClick={() => onOptionSelected(option.value)}
                        size={12}
                        style={{
                            cursor: 'pointer',
                            padding: '14px 20px',
                            borderRadius: 8,
                            transition: 'background-color 0.6s',
                            backgroundColor: isSelected ? runwayColors.white : 'transparent',
                        }}
                    >
                        {option.icon}
                        {option.label}
                    </Space>
                );
            })}
        </Space>
    );
};

const STAT_CATEGORY_FILTERS: FilterBarOption[] = [
    StatisticCategory.DevOps,
    StatisticCategory.Build,
    StatisticCategory.Work,
    StatisticCategory.StabilityAndHealth,
].map((statCategory) => ({
    label: displayTitleForStatCategory(statCategory),
    value: statCategory,
    icon: (
        <Icon
            component={iconForStatCategory(statCategory)}
            style={{
                color: colorForStatCategory(statCategory),
            }}
        />
    ),
}));

export const OrgOverviewAppSetsTabBar: React.FC<OrgOverviewAppSetsTabBarProps> = ({
    appSets,
    selectedAppSetIndex,
    onAppSetSelected,
    selectedPlatform,
    onPlatformSelected,
    selectedStatCategory,
    onStatCategorySelected,
    isLoading,
    calendarDatesHaveChanged,
    onFinish,
}) => {
    const selectedAppSet = appSets[selectedAppSetIndex];

    const selectedAppSetPlatforms =
        selectedAppSetIndex === 0 ? [] : (selectedAppSet?.apps.map((app) => app.platform) ?? []);

    const platformOptions: FilterBarOption[] = Object.values(AppPlatform)
        .map((platform) => ({
            label: displayTextForAppPlatform(platform),
            value: platform,
            icon: iconForPlatform(platform),
        }))
        .filter((option) => selectedAppSetPlatforms.includes(option.value as AppPlatform));

    const onPlatformOptionSelected = (optionValue: AppPlatform | 'all') => {
        if (optionValue === 'all') {
            onPlatformSelected(optionValue);
        } else {
            const appId = selectedAppSet?.apps.find((app) => app.platform === optionValue)?.id;

            onPlatformSelected(optionValue, appId);
        }
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
            }}
        >
            <div
                className="app-set-tab-bar"
                style={{
                    overflowX: 'scroll',
                }}
            >
                <div
                    style={{
                        gap: 10,
                    }}
                >
                    <ConfigProvider
                        theme={{
                            components: {
                                Tabs: {
                                    itemColor: 'inherit', // Reset tab item color
                                    itemActiveColor: 'inherit',
                                    itemHoverColor: 'inherit',
                                    inkBarColor: 'transparent', // Hide the active tab indicator
                                    horizontalItemPadding: '0', // Remove padding
                                    horizontalMargin: '0', // Remove margin
                                },
                            },
                        }}
                    >
                        <Tabs
                            destroyInactiveTabPane={false} // Keeps inactive tabs in the DOM
                            items={appSets.map((appSet, index) => ({
                                key: index.toString(),
                                forceRender: true,
                                label: (
                                    <AppSetTab
                                        {...appSet}
                                        isSelected={index === selectedAppSetIndex}
                                        isLoading={isLoading}
                                        onClick={() => onAppSetSelected(index)}
                                    />
                                ),
                            }))}
                            tabBarExtraContent={{
                                right: (
                                    <DatePickerTimeRange
                                        onFinish={onFinish}
                                        isLoading={isLoading}
                                        calendarDatesHaveChanged={calendarDatesHaveChanged}
                                    />
                                ),
                            }}
                        />
                    </ConfigProvider>
                </div>
            </div>
            <div
                style={{
                    backgroundColor: antGrey.grey3,
                    borderRadius: '0px 0px 8px 8px',
                    padding: 20,
                    width: '100%',
                    display: 'flex',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flex: 1,
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        rowGap: 20,
                        columnGap: 60,
                    }}
                >
                    <FilterBar
                        options={[
                            {
                                label: 'All metrics',
                                value: 'all',
                                icon: <AppstoreFilled style={{ color: runwayColors.primary }} />,
                            },
                            ...STAT_CATEGORY_FILTERS,
                        ]}
                        selectedOptionValue={selectedStatCategory}
                        onOptionSelected={onStatCategorySelected}
                    />

                    {platformOptions.length > 1 && (
                        <FilterBar
                            options={[
                                {
                                    label: 'All',
                                    value: 'all',
                                    icon: <AppstoreFilled style={{ color: runwayColors.primary }} />,
                                },
                                ...platformOptions,
                            ]}
                            selectedOptionValue={selectedPlatform}
                            onOptionSelected={onPlatformOptionSelected}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export const OrgOverviewAppMetricsTabBar: React.FC<OrgOverviewAppMetricsTabBarProps> = ({
    selectedStatCategory,
    onStatCategorySelected,
}) => {
    const { mode } = useThemeMode();
    const isDark = mode === 'dark';

    const tabItems = [
        {
            key: 'all',
            label: 'All metrics',
            icon: <AppstoreFilled style={{ color: runwayColors.primary }} />,
        },
        ...STAT_CATEGORY_FILTERS.map((filter) => ({
            key: filter.value,
            label: filter.label,
            icon: filter.icon,
        })),
    ];

    return (
        <div
            style={{
                backgroundColor: 'transparent',
                width: '100%',
                display: 'flex',
            }}
        >
            <ConfigProvider
                theme={{
                    components: {
                        Tabs: {
                            inkBarColor: 'transparent',
                            horizontalItemPadding: '0',
                        },
                    },
                }}
            >
                <Tabs
                    tabBarGutter={4}
                    activeKey={selectedStatCategory}
                    onChange={(activeKey) => onStatCategorySelected(activeKey as StatisticCategoryOrAll)}
                    items={tabItems.map((item) => ({
                        key: item.key,
                        label: (
                            <MetricCategoryTab
                                label={item.label}
                                icon={item.icon}
                                isSelected={selectedStatCategory === item.key}
                                isDark={isDark}
                            />
                        ),
                    }))}
                    tabBarStyle={{
                        margin: 0,
                        paddingTop: 12,
                        borderBottom: `1px solid ${antGrey.grey4}`,
                        width: '100%',
                    }}
                />
            </ConfigProvider>
        </div>
    );
};
